import React, { useState, useEffect, useRef } from 'react';
import { useMessaging } from '../../contexts/MessagingContext';
import supabase from '../../utils/supabaseClient';

const MessageThread = ({ conversation, currentUser, onBack }) => {
  const { markAsRead } = useMessaging();
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const messageContainerRef = useRef(null);
  
  // Scroll to the bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [conversation.messages]);
  
  // Mark unread messages as read when thread is opened
  useEffect(() => {
    const markThreadAsRead = async () => {
      // Collect IDs of unread messages in this thread that are sent to current user
      const unreadMessageIds = conversation.messages
        .filter(msg => !msg.is_read && msg.receiver_id === currentUser.id)
        .map(msg => msg.id);
      
      if (unreadMessageIds.length > 0) {
        // Mark these messages as read in the database
        await markAsRead(unreadMessageIds);
        
        // Update local conversation data
        conversation.messages.forEach(message => {
          if (unreadMessageIds.includes(message.id)) {
            message.is_read = true;
          }
        });
        
        // Reset unread count for this conversation
        conversation.unreadCount = 0;
      }
    };
    
    markThreadAsRead();
  }, [conversation, currentUser.id, markAsRead]);
  
  const scrollToBottom = () => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTop = messageContainerRef.current.scrollHeight;
    }
  };
  
  const handleSendReply = async (e) => {
    e.preventDefault();
    
    if (!replyText.trim()) {
      return;
    }
    
    try {
      setSending(true);
      setError('');
      
      // Get subject from latest message, with 'Re: ' prefix
      let subject = conversation.latestMessage.subject;
      if (!subject.startsWith('Re: ')) {
        subject = `Re: ${subject}`;
      }
      
      // Save the message to Supabase
      const { data, error: messageError } = await supabase
        .from('messages')
        .insert([
          {
            sender_id: currentUser.id,
            receiver_id: conversation.partnerId,
            subject: subject,
            content: replyText.trim(),
            is_read: false,
            parent_id: conversation.latestMessage.id
          }
        ]);
      
      if (messageError) throw messageError;
      
      // Clear the reply text
      setReplyText('');
      
      // Generate a temporary ID if data isn't returned properly
      const messageId = data && data[0] ? data[0].id : `temp-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
      
      // Add the new message to the conversation
      conversation.messages.push({
        id: messageId,
        sender_id: currentUser.id,
        receiver_id: conversation.partnerId,
        subject: subject,
        content: replyText.trim(),
        is_read: false,
        created_at: new Date().toISOString(),
        sender_name: currentUser.profile?.name && !currentUser.profile?.name.includes('@') ? 
                    currentUser.profile.name : 'Anonymous',
        sender_photo: currentUser.profile?.photo_url,
        receiver_name: conversation.partnerName,
        receiver_photo: conversation.partnerPhoto
      });
      
      // Update the latest message reference
      conversation.latestMessage = conversation.messages[conversation.messages.length - 1];
      
      setSending(false);
    } catch (err) {
      console.error('Error sending reply:', err);
      setError('Failed to send reply. Please try again.');
      setSending(false);
    }
  };
  
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center py-3 border-b">
        <button 
          onClick={onBack}
          className="flex items-center justify-center w-8 h-8 mr-2 text-gray-500 hover:text-gray-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex items-center">
          {conversation.partnerPhoto ? (
            <img 
              src={conversation.partnerPhoto} 
              alt={conversation.partnerName} 
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-gray-500 font-semibold">
                {conversation.partnerName && conversation.partnerName !== 'Anonymous' ? 
                  conversation.partnerName.charAt(0).toUpperCase() : 'A'}
              </span>
            </div>
          )}
          <h3 className="ml-2 text-sm font-medium text-gray-900">
            {conversation.partnerName}
          </h3>
        </div>
      </div>
      
      <div className="flex-1 overflow-y-auto py-2 px-1" id="messageContainer" ref={messageContainerRef}>
        <div className="space-y-4">
          {[...conversation.messages].sort((a, b) => 
            new Date(a.created_at) - new Date(b.created_at)
          ).map((message) => {
            const isOutgoing = message.sender_id === currentUser.id;
            
            return (
              <div key={message.id} className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-xs sm:max-w-md rounded-lg p-3 ${isOutgoing ? 'bg-primary-100 text-primary-900' : 'bg-gray-100'}`}>
                  <div className="mb-1 flex justify-between items-baseline">
                    <span className="text-xs text-gray-500">
                      {new Date(message.created_at).toLocaleString()}
                    </span>
                    {!isOutgoing && !message.is_read && (
                      <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        New
                      </span>
                    )}
                  </div>
                  <p className="font-medium text-sm">{message.subject}</p>
                  <p className="whitespace-pre-wrap text-sm mt-1 break-words">{message.content}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      <div className="mt-auto pt-3 border-t">
        {error && (
          <div className="mb-2 p-2 text-sm text-red-700 bg-red-100 rounded-md">
            {error}
          </div>
        )}
        
        <form onSubmit={handleSendReply} className="flex items-end">
          <div className="flex-1">
            <label htmlFor="reply" className="sr-only">Reply</label>
            <textarea 
              id="reply"
              rows="3"
              className="shadow-sm focus:ring-primary-500 focus:border-primary-500 block w-full sm:text-sm border-gray-300 rounded-md"
              placeholder="Type your reply..."
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              required
            />
          </div>
          <div className="ml-3">
            <button
              type="submit"
              disabled={sending || !replyText.trim()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
            >
              {sending ? (
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                </svg>
              )}
              Send
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MessageThread; 